import uniqueId from 'lodash/uniqueId';
import cx from 'classnames';
import React, { forwardRef, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactModal, { OnAfterOpenCallback } from 'react-modal';
import { IntercomWrapper } from 'services/intercom';
import { changePhoneNumberDynamicallyAfterTimeout } from 'utils/InvocaUtils';

import { IconType } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { Modal, ModalWrapperProps as ModalLegacyProps } from '../Modal/Modal';
import { TitleAndDescription } from './components/TitleAndDescription';
import { ModalActions, StructuredModalAction } from './components/ModalActions';

import styles from './StructuredModal.module.scss';

type CoreStructuredModalProps = Omit<ModalLegacyProps, 'isOpen'> & {
  title?: string;
  description?: string;
  actions?: StructuredModalAction[];
};

interface IStructuredModalPropsWithState extends CoreStructuredModalProps {
  /**
   * @example
   * const [isOpen, setIsOpen] = useState(false);
   * <StructuredModal
   *   modalState={[ isOpen, setIsOpen ]}
   *   // ...other props
   * />
   */
  modalState: StructuredModalState;
  className?: string;
}

interface IStructuredModalPropsWithOpen extends CoreStructuredModalProps {
  isOpen: boolean;
  className?: string;
}

export type StructuredModalProps = IStructuredModalPropsWithState | IStructuredModalPropsWithOpen;
// eslint-disable-next-line  @typescript-eslint/no-explicit-any
export type StructuredModalState = [boolean, (state: boolean) => any];
// eslint-disable-next-line  @typescript-eslint/no-explicit-any
type StructuredModalStateWithOptionalState = [boolean, ((state: boolean) => any) | undefined];

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const generateAriaAttributes = (aria: any, uid: React.MutableRefObject<string>, title?: string) => {
  if (!aria) {
    return {
      modal: true,
      labelledby: title ? `structured-modal-title-${uid.current}` : undefined,
    };
  }
  return aria;
};

/**
 * Displays a modern modal
 */
export const StructuredModal = forwardRef<ReactModal, StructuredModalProps>(
  (
    {
      title,
      description,
      actions,
      children,
      closeTimeoutMS = 500,
      aria,
      shouldCloseOnEsc = true,
      className,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const uid = useRef(uniqueId());
    const modalState: StructuredModalStateWithOptionalState = [
      'isOpen' in props ? props.isOpen : props.modalState[0],
      'isOpen' in props ? undefined : props.modalState[1],
    ];
    const modalActive = modalState[0];
    const setModalActive = modalState[1];

    aria = generateAriaAttributes(aria, uid, title);
    const focusElementId = `structured-modal-close-${uid.current}`;

    const handleOpen: OnAfterOpenCallback = obj => {
      if (props.onAfterOpen) props.onAfterOpen(obj);
      else {
        document.getElementById(focusElementId)?.focus();
      }
      IntercomWrapper.hideLauncher();
    };

    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    const handleClose = (obj?: any) => {
      if (setModalActive) setModalActive(false);
      if (props.onRequestClose && !setModalActive) props.onRequestClose(obj);
      IntercomWrapper.showLauncher();
    };

    const handleAction = (modalAction: StructuredModalAction) => {
      if ('isCloseButton' in modalAction && modalAction.isCloseButton) {
        handleClose();
      } else if ('action' in modalAction && modalAction.action) {
        const result = modalAction.action();
        if (!result) {
          handleClose();
        }
      }
    };

    const modalContentClasses: ReactModal.Classes = {
      base: cx(styles.modal, className),
      afterOpen: styles.modalLoaded,
      beforeClose: styles.modalClosing,
    };

    const modalOverlayClasses: ReactModal.Classes = {
      base: cx(styles.overlay),
      afterOpen: styles.overlayLoaded,
      beforeClose: styles.overlayClosing,
    };

    useEffect(() => {
      changePhoneNumberDynamicallyAfterTimeout();
    }, [description, title, modalActive]);

    return (
      <Modal
        ref={ref}
        closeTimeoutMS={closeTimeoutMS}
        isOpen={modalActive}
        className={modalContentClasses}
        overlayClassName={modalOverlayClasses}
        onAfterOpen={handleOpen}
        onRequestClose={handleClose}
        focusElementId={focusElementId}
        shouldCloseOnEsc={shouldCloseOnEsc}
        shouldFocusAfterRender
        aria={aria}
        testId={props.testId || 'structured-modal'}
        {...props}
      >
        <IconButton
          id={focusElementId}
          onClick={handleClose}
          className={styles.closeIcon}
          aria-label={t('close')}
          type={IconType.CLOSE}
        />
        <TitleAndDescription title={title} description={description} />
        {children}
        <ModalActions actions={actions} handleAction={handleAction} />
      </Modal>
    );
  },
);
